import { useMemo, useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";

import type { AcademicTerm, AcademicYear } from "../../services/academicSetupService";
import { examinationService, type Exam, type ExamSchedule, type ExamType } from "../../services/examinationService";
import { apiErrorMessage } from "../../utils/apiError";
import AIAssistField from "../AIAssistField";
import { Field, FormSection } from "../FormLayout";
import { FormModal } from "../FormModal";
import { StaffPicker } from "../register/StaffPicker";
import { clock, day } from "./examLogic";

export type SubjectOption = { id: number; name: string; code?: string; department_name?: string | null; department_head?: number | null; excluded_levels?: string[] };
export type ClassOption = { id: number; name: string; level: string; academic_year?: number };
export const termLabel = (term: Pick<AcademicTerm, "term" | "academic_year_name">) => `${term.term.replace("term_", "Term ")} · ${term.academic_year_name}`;

/** Classes grouped by level, ticked one by one or a whole level at once. */
function ClassPicker({ classes, value, onChange, excluded = [] }: { classes: ClassOption[]; value: Set<number>; onChange: (next: Set<number>) => void; excluded?: string[] }) {
  const byLevel = useMemo(() => {
    const groups = new Map<string, ClassOption[]>();
    for (const item of classes) if (!excluded.includes(item.level)) groups.set(item.level, [...(groups.get(item.level) ?? []), item]);
    return groups;
  }, [classes, excluded]);
  const toggle = (id: number) => {
    const next = new Set(value);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(next);
  };
  const toggleLevel = (items: ClassOption[]) => {
    const all = items.every((item) => value.has(item.id));
    const next = new Set(value);
    items.forEach((item) => (all ? next.delete(item.id) : next.add(item.id)));
    onChange(next);
  };
  if (!byLevel.size) return <p className="mt-1 rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-600">No classes for this academic year.</p>;
  return (
    <div className="mt-1 divide-y divide-gray-100 rounded-lg border border-gray-200">
      {[...byLevel.entries()].map(([level, items]) => (
        <div key={level} className="flex flex-wrap items-center gap-2 px-3 py-2">
          <button type="button" onClick={() => toggleLevel(items)} className="w-24 text-left text-xs font-semibold text-primary-700 hover:underline">{level}</button>
          {items.map((item) => (
            <label key={item.id} className={`flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs ${value.has(item.id) ? "border-primary-300 bg-primary-50 text-primary-800" : "border-gray-200 text-gray-700"}`}>
              <input type="checkbox" checked={value.has(item.id)} onChange={() => toggle(item.id)} className="h-3.5 w-3.5 rounded border-gray-300" />
              {item.name}
            </label>
          ))}
        </div>
      ))}
    </div>
  );
}

/** A new paper (saved as a draft) or changes to one still being marked. */
export function PaperForm({ exam, types, subjects, years, terms, classes, me, isManager, onClose, onDone }: {
  exam?: Exam;
  types: ExamType[];
  subjects: SubjectOption[];
  years: AcademicYear[];
  terms: AcademicTerm[];
  classes: ClassOption[];
  me?: number;
  isManager: boolean;
  onClose: () => void;
  onDone: (row: Exam) => void;
}) {
  const current = years.find((row) => row.is_current) ?? years[0];
  const [year, setYear] = useState(exam ? String(exam.academic_year) : current ? String(current.id) : "");
  const [typeId, setTypeId] = useState(exam ? String(exam.exam_type) : "");
  const [subjectId, setSubjectId] = useState(exam ? String(exam.subject) : "");
  const [picked, setPicked] = useState<Set<number>>(new Set(exam?.classes ?? []));
  const [start, setStart] = useState(clock(exam?.start_time) || "09:00");
  const [minutes, setMinutes] = useState(String(exam?.duration_minutes ?? 120));
  const [instructions, setInstructions] = useState(exam?.instructions ?? "");
  const [file, setFile] = useState<File | null>(null);
  const type = types.find((row) => String(row.id) === typeId);
  const subject = subjects.find((row) => String(row.id) === subjectId);
  const yearClasses = classes.filter((row) => !row.academic_year || String(row.academic_year) === year);
  const save = useMutation({
    mutationFn: (data: Partial<Exam>) => (exam ? examinationService.updateExam(exam.id, data, file) : examinationService.createExam(data, file)),
    onSuccess: onDone,
  });
  const end = (() => {
    const [hours, mins] = start.split(":").map(Number);
    const total = hours * 60 + mins + (Number(minutes) || 0);
    return `${String(Math.floor(total / 60) % 24).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
  })();
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const text = (name: string) => String(data.get(name) ?? "").trim();
    save.mutate({
      name: text("name"),
      exam_type: Number(typeId),
      subject: Number(subjectId),
      academic_year: Number(year),
      term: text("term") ? Number(text("term")) : null,
      classes: [...picked],
      exam_date: text("exam_date"),
      start_time: start,
      end_time: end,
      duration_minutes: Number(minutes),
      total_marks: Number(text("total_marks")),
      passing_marks: Number(text("passing_marks")),
      venue: text("venue") || undefined,
      instructions: instructions.trim() || undefined,
      ...(exam ? {} : { status: "draft" as const, is_published: false }),
    });
  };
  const majorBlocked = !exam && type?.is_major_exam && subject && !isManager && subject.department_head !== me;
  return (
    <FormModal
      id="exam-paper"
      title={exam ? `Change ${exam.name}` : "New paper"}
      busy={save.isPending}
      error={save.isError ? apiErrorMessage(save.error, "The paper could not be saved.") : undefined}
      saveLabel={exam ? "Save changes" : "Save the paper"}
      note={exam ? "Papers are locked once their results are submitted." : "Candidates come from the exam period, or from the classes chosen here."}
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="The paper" divided={false}>
        <Field label="Name" className="sm:col-span-2" hint="e.g. P7 Mathematics, End of Term II.">
          <input required name="name" maxLength={200} defaultValue={exam?.name} className="input mt-1" />
        </Field>
        <Field label="Kind" hint={type?.is_major_exam ? "A major exam: set by the subject's head of department or academic leadership." : undefined}>
          <select required value={typeId} onChange={(e) => setTypeId(e.target.value)} className="input mt-1">
            <option value="">Choose a kind</option>
            {types.filter((row) => row.is_active || String(row.id) === typeId).map((row) => <option key={row.id} value={row.id}>{row.name}{row.is_major_exam ? " (major)" : ""}</option>)}
          </select>
        </Field>
        <Field label="Subject">
          <select required value={subjectId} onChange={(e) => setSubjectId(e.target.value)} className="input mt-1">
            <option value="">Choose a subject</option>
            {subjects.map((row) => <option key={row.id} value={row.id}>{row.name}{row.department_name ? ` · ${row.department_name}` : ""}</option>)}
          </select>
        </Field>
        {majorBlocked && <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900 sm:col-span-2">Only {subject?.department_name || "the owning department"}'s head can set a major exam for {subject?.name}.</p>}
      </FormSection>

      <FormSection title="When">
        <Field label="Academic year">
          <select required value={year} onChange={(e) => { setYear(e.target.value); setPicked(new Set()); }} className="input mt-1">
            {years.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
          </select>
        </Field>
        <Field label="Term" optional hint="Exam periods find their papers by term.">
          <select name="term" defaultValue={exam?.term ?? ""} className="input mt-1">
            <option value="">No term</option>
            {terms.filter((row) => String(row.academic_year) === year).map((row) => <option key={row.id} value={row.id}>{termLabel(row)}</option>)}
          </select>
        </Field>
        <Field label="Date" hint="An exam period can reschedule it.">
          <input required type="date" name="exam_date" defaultValue={day(exam?.exam_date)} className="input mt-1" />
        </Field>
        <Field label="Starts">
          <input required type="time" value={start} onChange={(e) => setStart(e.target.value)} className="input mt-1" />
        </Field>
        <Field label="Time allowed (minutes)" hint={`Ends at ${end}.`}>
          <input required type="number" min={1} value={minutes} onChange={(e) => setMinutes(e.target.value)} className="input mt-1" />
        </Field>
        <Field label="Venue" optional>
          <input name="venue" defaultValue={exam?.venue ?? ""} className="input mt-1" />
        </Field>
      </FormSection>

      <FormSection title="Marks and classes">
        <Field label="Out of">
          <input required type="number" min={1} name="total_marks" defaultValue={exam?.total_marks ?? type?.total_marks ?? 100} key={`total-${typeId}`} className="input mt-1" />
        </Field>
        <Field label="Pass mark">
          <input required type="number" min={0} name="passing_marks" defaultValue={exam?.passing_marks ?? type?.passing_marks ?? 50} key={`pass-${typeId}`} className="input mt-1" />
        </Field>
        <Field label="Classes sitting it" as="div" className="sm:col-span-2" hint={subject?.excluded_levels?.length ? `${subject.name} is not taught at: ${subject.excluded_levels.join(", ")}.` : "Click a level to tick all its streams."}>
          <ClassPicker classes={yearClasses} value={picked} onChange={setPicked} excluded={subject?.excluded_levels ?? []} />
        </Field>
        <Field label="Instructions to candidates" optional as="div" className="sm:col-span-2">
          <AIAssistField
            rows={3}
            value={instructions}
            onChange={(next) => setInstructions(next)}
            placeholder={"1. Answer all questions in Section A.\n2. Write your learner number on every page."}
            context={exam ? { key: "examination.note", params: { kind: "instructions", id: exam.id } } : undefined}
            draftLabel="Word them"
            draftHint="Jot the rules first; it numbers and words them, adding nothing."
          />
        </Field>
        <Field label="The paper itself" optional className="sm:col-span-2" hint={exam?.document ? "A file is uploaded; choose another to replace it." : "PDF or Word, for leadership to review and print."}>
          <input type="file" accept=".pdf,.doc,.docx" onChange={(e) => setFile(e.target.files?.[0] ?? null)} className="input mt-1" />
        </Field>
      </FormSection>
    </FormModal>
  );
}

/** A kind of exam: its default length and marks, and whether it is major. */
export function ExamTypeForm({ type, onClose, onDone }: { type?: ExamType; onClose: () => void; onDone: (row: ExamType) => void }) {
  const save = useMutation({ mutationFn: (data: Partial<ExamType>) => (type ? examinationService.updateExamType(type.id, data) : examinationService.createExamType(data)), onSuccess: onDone });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const text = (name: string) => String(data.get(name) ?? "").trim();
    save.mutate({
      name: text("name"),
      code: text("code"),
      description: text("description"),
      duration_minutes: Number(text("duration_minutes")),
      total_marks: Number(text("total_marks")),
      passing_marks: Number(text("passing_marks")),
      weight_percentage: text("weight_percentage") || "0",
      is_major_exam: data.get("is_major_exam") === "on",
      is_mandatory: data.get("is_mandatory") === "on",
      is_active: type ? data.get("is_active") === "on" : true,
    });
  };
  return (
    <FormModal id="exam-type" title={type ? `Change ${type.name}` : "New kind of exam"} busy={save.isPending} error={save.isError ? apiErrorMessage(save.error, "It could not be saved.") : undefined} saveLabel={type ? "Save changes" : "Add it"} onClose={onClose} onSubmit={submit}>
      <FormSection title="The kind of exam" divided={false}>
        <Field label="Name" hint="e.g. End of Term, Mid Term, Class test.">
          <input required name="name" maxLength={100} defaultValue={type?.name} className="input mt-1" />
        </Field>
        <Field label="Code" hint="Short and unique, e.g. EOT.">
          <input required name="code" maxLength={20} defaultValue={type?.code} className="input mt-1" />
        </Field>
        <Field label="Usual length (minutes)">
          <input required type="number" min={1} name="duration_minutes" defaultValue={type?.duration_minutes ?? 120} className="input mt-1" />
        </Field>
        <Field label="Weight in the term (%)" optional>
          <input type="number" min={0} max={100} step="0.01" name="weight_percentage" defaultValue={type?.weight_percentage ?? "0"} className="input mt-1" />
        </Field>
        <Field label="Out of">
          <input required type="number" min={1} name="total_marks" defaultValue={type?.total_marks ?? 100} className="input mt-1" />
        </Field>
        <Field label="Pass mark">
          <input required type="number" min={0} name="passing_marks" defaultValue={type?.passing_marks ?? 50} className="input mt-1" />
        </Field>
        <Field label="Description" optional className="sm:col-span-2">
          <textarea name="description" rows={2} defaultValue={type?.description} className="input mt-1" />
        </Field>
        <label className="flex items-start gap-2 text-sm text-gray-700 sm:col-span-2">
          <input type="checkbox" name="is_major_exam" defaultChecked={type?.is_major_exam} className="mt-0.5 h-4 w-4 rounded border-gray-300" />
          <span>A major exam <span className="block text-xs text-gray-500">Beginning, mid or end of term: only the subject's head of department or academic leadership sets these papers.</span></span>
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" name="is_mandatory" defaultChecked={type?.is_mandatory ?? true} className="h-4 w-4 rounded border-gray-300" /> Every learner sits it
        </label>
        {type && (
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" name="is_active" defaultChecked={type.is_active} className="h-4 w-4 rounded border-gray-300" /> In use
          </label>
        )}
      </FormSection>
    </FormModal>
  );
}

/** An exam period: the weeks the term's papers are sat in. */
export function PeriodForm({ years, terms, onClose, onDone }: { years: AcademicYear[]; terms: AcademicTerm[]; onClose: () => void; onDone: (row: ExamSchedule) => void }) {
  const current = years.find((row) => row.is_current) ?? years[0];
  const [year, setYear] = useState(current ? String(current.id) : "");
  const [termId, setTermId] = useState("");
  const term = terms.find((row) => String(row.id) === termId);
  const save = useMutation({ mutationFn: examinationService.createExamSchedule, onSuccess: onDone });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    save.mutate({ name: String(data.get("name")).trim(), academic_year: Number(year), term: Number(termId), start_date: String(data.get("start_date")), end_date: String(data.get("end_date")), description: "", is_active: true, is_published: false });
  };
  return (
    <FormModal id="exam-period" title="New exam period" busy={save.isPending} error={save.isError ? apiErrorMessage(save.error, "The period could not be created.") : undefined} saveLabel="Create it" note="Next: timetable its papers, then seat the candidates." onClose={onClose} onSubmit={submit}>
      <FormSection title="The period" divided={false}>
        <Field label="Name" className="sm:col-span-2" hint="e.g. End of Term II exams 2026.">
          <input required name="name" maxLength={200} className="input mt-1" />
        </Field>
        <Field label="Academic year">
          <select required value={year} onChange={(e) => { setYear(e.target.value); setTermId(""); }} className="input mt-1">
            {years.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
          </select>
        </Field>
        <Field label="Term" hint="The papers set for this term are timetabled into the period.">
          <select required value={termId} onChange={(e) => setTermId(e.target.value)} className="input mt-1">
            <option value="">Choose a term</option>
            {terms.filter((row) => String(row.academic_year) === year).map((row) => <option key={row.id} value={row.id}>{termLabel(row)}</option>)}
          </select>
        </Field>
        <Field label="First day" hint={term ? `The term runs ${day(term.start_date)} to ${day(term.end_date)}.` : undefined}>
          <input required type="date" name="start_date" min={term?.start_date} max={term?.end_date} className="input mt-1" />
        </Field>
        <Field label="Last day" hint="Papers go into weekday mornings (9:00) and afternoons (14:00).">
          <input required type="date" name="end_date" min={term?.start_date} max={term?.end_date} className="input mt-1" />
        </Field>
      </FormSection>
    </FormModal>
  );
}

/** Seating the period's candidates: which classes sit, where, and who invigilates. */
export function CandidatesForm({ period, classes, invigilators, onClose, onDone }: {
  period: ExamSchedule;
  classes: ClassOption[];
  invigilators: { id: number; full_name: string; role: string }[];
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [team, setTeam] = useState<number[]>([]);
  const save = useMutation({
    mutationFn: (data: { class_ids: number[]; invigilator_ids: number[]; venue: string; room_capacity: number }) => examinationService.generateCandidates(period.id, data),
    onSuccess: (result: { message?: string }) => onDone(result.message ?? "Candidates seated."),
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    save.mutate({ class_ids: [...picked], invigilator_ids: team, venue: String(data.get("venue")).trim(), room_capacity: Number(data.get("room_capacity")) });
  };
  return (
    <FormModal id="exam-candidates" title={`Seat the candidates: ${period.name}`} busy={save.isPending} error={save.isError ? apiErrorMessage(save.error, "The candidates could not be seated.") : undefined} saveLabel="Seat them" note="Every timetabled paper gets these classes; seats are numbered hall by hall." onClose={onClose} onSubmit={submit}>
      <FormSection title="Who sits" divided={false}>
        <Field label="Classes" as="div" className="sm:col-span-2">
          <ClassPicker classes={classes.filter((row) => !row.academic_year || row.academic_year === period.academic_year)} value={picked} onChange={setPicked} />
        </Field>
      </FormSection>
      <FormSection title="Where and who watches">
        <Field label="Hall" hint="Further halls are numbered after it.">
          <input required name="venue" defaultValue="Main Hall" className="input mt-1" />
        </Field>
        <Field label="Seats per hall">
          <input required type="number" min={1} name="room_capacity" defaultValue={40} className="input mt-1" />
        </Field>
        <Field label="Invigilators" as="div" className="sm:col-span-2" hint="Shared out hall by hall.">
          <StaffPicker staff={invigilators} value={team} onChange={setTeam} />
        </Field>
      </FormSection>
    </FormModal>
  );
}
